import type { Customer } from "../modals/types";
import { DataTable } from "@/components/shared/data-table";
import { createColumns } from "./columns";

interface CustomersTableProps {
  customers: Customer[];
  loading: boolean;
  error?: string | null;
  onRetry?: () => void;
  onEdit: (customer: Customer) => void;
  onArchive: (customer: Customer) => void;
  onUnarchive: (customer: Customer) => void;
  onPromote?: (customer: Customer) => void;
  onMerge?: (customer: Customer) => void;
  onCreateNew?: () => void;
  onRefresh?: () => void;
  refreshing?: boolean;
  onExport?: () => void;
  manualPagination?: boolean;
  pageCount?: number;
  totalResults?: number;
  currentPage?: number;
  onPageChange?: (pageIndex: number) => void;
  onSearch?: (query: string) => void;
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageSizeChange?: (pageSize: number) => void;
  fillHeight?: boolean;
}

export function CustomersTable({
  customers,
  loading,
  error,
  onRetry,
  onEdit,
  onArchive,
  onUnarchive,
  onPromote,
  onMerge,
  onCreateNew,
  onRefresh,
  refreshing,
  onExport,
  manualPagination,
  pageCount,
  totalResults,
  currentPage,
  onPageChange,
  onSearch,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  fillHeight,
}: CustomersTableProps) {
  const columns = createColumns({
    onEdit,
    onArchive,
    onUnarchive,
    onPromote,
    onMerge,
  });

  return (
    <DataTable
      columns={columns}
      data={customers}
      loading={loading}
      error={error}
      onRetry={onRetry}
      onCreateNew={onCreateNew}
      createButtonLabel="Add Customer"
      createButtonIcon="user"
      onRefresh={onRefresh}
      refreshing={refreshing}
      onExport={onExport}
      searchPlaceholder="Filter by name, brand, or sales channel..."
      globalFilterFn={(row, _columnId, filterValue) => {
        const s = String(filterValue).toLowerCase();
        return (
          String(row.getValue("name") || "")
            .toLowerCase()
            .includes(s) ||
          String(row.getValue("brand") || "")
            .toLowerCase()
            .includes(s) ||
          String(row.getValue("sales_channel") || "")
            .toLowerCase()
            .includes(s)
        );
      }}
      loadingMessage="Loading customers..."
      emptyMessage="No customers found"
      manualPagination={manualPagination}
      preservePageOnDataRefresh={true}
      initialSorting={[{ id: "name", desc: false }]}
      pageCount={pageCount}
      totalResults={totalResults}
      currentPage={currentPage}
      onPageChange={onPageChange}
      onSearch={onSearch}
      pageSize={pageSize}
      pageSizeOptions={pageSizeOptions}
      onPageSizeChange={onPageSizeChange}
      fillHeight={fillHeight}
      enableRowSelection={true}
    />
  );
}
